import { FC } from 'react';
import {
    CaseloadReading,
    IAgencyDigest,
    IHistoryProgress,
    describeCaseload,
    describeHistoryRule
} from '@innerhive-internal/innerhive-common';
import { IAvatarIdentity } from '../../avatar/IAvatarIdentity';
import { CaseloadReadingFilters } from './CaseloadReadingFilters';
import { HoneycombCaseloadMap, IHoneycombCell } from './HoneycombCaseloadMap';
import { InsightsCard } from './InsightsCard';
import { InsightsInfoTip } from './InsightsInfoTip';
import { BeaSays } from './BeaSays';
import { NeedsHistory } from './NeedsHistory';

/**
 * The caseload at a glance: one cell per client, in the caseload's own order rather than worst-first,
 * and Bea's sentence about it.
 *
 * The honeycomb is the one surface where a client keeps the same position from visit to visit, which
 * is what lets someone find a person on it. Re-sorting it by severity would move everybody every
 * time anything changed.
 *
 * The cells are the digest's own readings, which fold a client with less raised than usual into
 * steady; Bea's sentence under them may still say such a client has recovered, and the Outcomes
 * Report counts them. The colour key above the map counts each reading and, on screen, narrows the
 * page to it; it stands even while the map waits on history, since an incident needs none.
 */
export const CaseloadSummaryReport: FC<{
    digest: IAgencyDigest;
    history: IHistoryProgress;
    /** When the page is narrowed, the clients still in scope. Everyone else stays but recedes. */
    matchingIds?: string[];
    /** The reading the page is narrowed to, when it is. */
    readingNarrowed?: CaseloadReading;
    /** Whether the page is narrowed at all, by a reading or otherwise. */
    narrowed?: boolean;
    /** How each client is drawn, with the audited link to a hosted photo. */
    clientAvatar: (careMapId: string) => IAvatarIdentity | undefined;
    /** The largest a map cell is drawn, where the page has a set amount of room. */
    cellRadius?: number;
    onOpenClient: (careMapId: string) => void;
    /** Absent on paper, where the key only reads. */
    onToggleReading?: (reading: CaseloadReading) => void;
    onClearFilter?: () => void;
}> = ({ digest, history, matchingIds, readingNarrowed, narrowed = false, clientAvatar, cellRadius, onOpenClient, onToggleReading, onClearFilter }) => {
    const cells: IHoneycombCell[] = digest.baselines.map(baseline => ({
        careMapId: baseline.careMapId,
        clientName: baseline.clientName,
        reading: baseline.reading,
        identity: clientAvatar(baseline.careMapId)
    }));
    const counts: { [key in CaseloadReading]: number } = { incident: 0, worthWatching: 0, steady: 0, unknown: 0 };
    for (const baseline of digest.baselines) {
        counts[baseline.reading]++;
    }

    return (
        <InsightsCard
            heading="Caseload Summary"
            action={(
                <InsightsInfoTip label="About the caseload summary" summary="One hexagon per client, coloured by their reading: an incident, more raised than their own usual, steady, or not enough on record to compare. Press a reading in the key to narrow the page to those clients.">
                    {!history.hasEnough && describeHistoryRule(history)}
                </InsightsInfoTip>
            )}
        >
            <CaseloadReadingFilters
                counts={counts}
                clientCount={digest.clientCount}
                hasHistory={history.hasEnough}
                filter={readingNarrowed}
                narrowed={narrowed}
                onToggleReading={onToggleReading}
                onClear={onClearFilter}
            />

            <NeedsHistory history={history} panel="caseload">
                <HoneycombCaseloadMap
                    cells={cells}
                    matchingIds={matchingIds}
                    cellRadius={cellRadius}
                    onSelectClient={onOpenClient}
                />

                <BeaSays panel="caseload">{describeCaseload(digest)}</BeaSays>
            </NeedsHistory>
        </InsightsCard>
    );
};
